// =================================================================
// MailInbox.js — Boîte mail plein écran (connecteur email IMAP/SMTP)
// P7.3 (2e cercle ITRIIA) — premier compte contact@lavoixdorchies.fr
// =================================================================
// Overlay plein écran : dossiers (INBOX + Sent) · liste messages ·
// panneau lecture (HTML sandboxé + PJ base64) · composeur avec bouton
// « Envoyer » explicite. Tout passe par l'endpoint générique
// POST /api/connectors/projects/:projectId/email/invoke {payload:{action}}.
//
// Carte blanche (E5) : l'envoi n'a lieu que sur clic « Envoyer » de
// l'utilisateur. L'IA ne déclenche jamais d'envoi (compose_mail = draft).
// =================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  X, RefreshCw, Inbox, Send, Mail, Settings, PenSquare,
  Search, Paperclip, Download, ArrowLeft, Reply, Loader2,
} from 'lucide-react';
import { toast } from '../ui/Toast';

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('oracle_token')}` },
});

function formatDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const now = new Date();
    const sameDay = dt.toDateString() === now.toDateString();
    return sameDay
      ? dt.toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })
      : dt.toLocaleDateString('fr', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

function addressLabel(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '(inconnu)';
  return arr.map(a => a.name || a.address || '').filter(Boolean).join(', ');
}

function downloadAttachment(att) {
  if (!att.content_base64) {
    toast.warning('Pièce jointe trop volumineuse pour le téléchargement inline (V1).');
    return;
  }
  try {
    const chars = atob(att.content_base64);
    const bytes = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    const blob = new Blob([bytes], { type: att.contentType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || 'piece-jointe';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Téléchargement impossible : ' + e.message);
  }
}

const MailInbox = ({ projectId, connectorName = 'Email', onClose, onOpenSettings, initialDraft = null }) => {
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);   // mail complet ouvert
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(Boolean(initialDraft));

  // Composeur
  const [draft, setDraft] = useState(initialDraft || { to: '', cc: '', subject: '', body_text: '' });
  const [sending, setSending] = useState(false);

  const invokeMail = useCallback(async (action, extra = {}) => {
    try {
      const res = await axios.post(
        `/api/connectors/projects/${projectId}/email/invoke`,
        { payload: { action, ...extra } },
        authHeader(),
      );
      const data = res.data || {};
      if (data.ok === false) {
        throw new Error(data.error || 'Erreur connecteur mail');
      }
      return data.body || {};
    } catch (e) {
      // Sur HTTP non-2xx (ex: 502), axios throw avant le check data.ok :
      // on remonte le vrai message backend (data.error/detail) plutôt qu'un
      // générique « Request failed with status code 502 ».
      const backendErr = e.response?.data?.error || e.response?.data?.detail;
      throw new Error(backendErr || e.message || 'Erreur connecteur mail');
    }
  }, [projectId]);

  // ── Chargement dossiers (une fois) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await invokeMail('folders');
        if (cancelled) return;
        const list = body.folders || [];
        // INBOX puis Sent puis le reste
        const rank = (f) => {
          if (f.specialUse === '\\Sent' || /^(sent|envoy)/i.test(f.name || f.path)) return 1;
          if ((f.path || '').toUpperCase() === 'INBOX') return 0;
          return 2;
        };
        list.sort((a, b) => rank(a) - rank(b));
        setFolders(list);
      } catch (e) {
        toast.error('Dossiers : ' + e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [invokeMail]);

  // ── Chargement liste messages ──
  const loadMessages = useCallback(async (folder = activeFolder, q = '') => {
    setLoadingList(true);
    setSelected(null);
    try {
      const body = await invokeMail('messages', { folder, limit: 30, query: q || undefined });
      setMessages(body.messages || []);
      setTotal(body.total || 0);
    } catch (e) {
      toast.error('Messages : ' + e.message);
      setMessages([]);
    } finally {
      setLoadingList(false);
    }
  }, [invokeMail, activeFolder]);

  useEffect(() => { loadMessages('INBOX'); /* initial */ // eslint-disable-next-line
  }, []);

  const openFolder = (folder) => {
    setActiveFolder(folder);
    setQuery('');
    setComposing(false);
    loadMessages(folder, '');
  };

  const openMessage = async (m) => {
    setLoadingMsg(true);
    setComposing(false);
    try {
      const body = await invokeMail('message', { folder: activeFolder, uid: m.uid });
      setSelected(body);
      // marque comme lu côté liste
      setMessages(prev => prev.map(x => x.uid === m.uid ? { ...x, seen: true } : x));
    } catch (e) {
      toast.error('Lecture : ' + e.message);
    } finally {
      setLoadingMsg(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    loadMessages(activeFolder, query.trim());
  };

  const startReply = () => {
    if (!selected) return;
    const fromAddr = (selected.from && selected.from[0] && selected.from[0].address) || '';
    setDraft({
      to: fromAddr,
      cc: '',
      subject: selected.subject ? (/^re:/i.test(selected.subject) ? selected.subject : `Re: ${selected.subject}`) : '',
      body_text: `\n\n--- Le ${formatDate(selected.date)}, ${addressLabel(selected.from)} a écrit ---\n${(selected.text || '').slice(0, 2000)}`,
      inReplyTo: selected.messageId || undefined,
    });
    setComposing(true);
  };

  const startCompose = () => {
    setDraft({ to: '', cc: '', subject: '', body_text: '' });
    setComposing(true);
  };

  const sendDraft = async () => {
    if (!draft.to || !draft.to.trim()) { toast.warning('Renseigne au moins un destinataire.'); return; }
    setSending(true);
    try {
      const body = await invokeMail('send', {
        to: draft.to.trim(),
        cc: draft.cc ? draft.cc.trim() : undefined,
        subject: draft.subject || '(sans objet)',
        html: draft.body_html || undefined,
        text: draft.body_text || undefined,
        inReplyTo: draft.inReplyTo || undefined,
      });
      if (body && body.sent) {
        toast.success('Mail envoyé ✓');
        setComposing(false);
        setDraft({ to: '', cc: '', subject: '', body_text: '' });
      } else {
        toast.error('Envoi non confirmé par le serveur.');
      }
    } catch (e) {
      toast.error('Envoi : ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const folderIcon = (f) => {
    if (f.specialUse === '\\Sent' || /^(sent|envoy)/i.test(f.name || f.path)) return Send;
    if ((f.path || '').toUpperCase() === 'INBOX') return Inbox;
    return Mail;
  };

  const C = useMemo(() => ({
    bg: 'var(--bg)',
    card: 'var(--card-bg)',
    border: 'var(--card-border)',
    text: 'var(--text)',
    muted: 'var(--text-secondary)',
    faint: 'var(--text-muted, #6a6a85)',
    accent: 'var(--accent, #1D4ED8)',
    accentBg: 'rgba(29,78,216,0.10)',
  }), []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100, background: C.bg,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px',
        borderBottom: `1px solid ${C.border}`, flexShrink: 0,
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: C.accentBg, display: 'grid', placeItems: 'center' }}>
          <Mail size={18} color={C.accent} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Boîte mail</div>
          <div style={{ fontSize: 12, color: C.faint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{connectorName}</div>
        </div>
        <button onClick={startCompose} style={btnPrimary(C)}>
          <PenSquare size={15} /> Nouveau
        </button>
        <button onClick={() => loadMessages(activeFolder, query)} title="Rafraîchir" style={btnGhost(C)}>
          <RefreshCw size={15} />
        </button>
        {onOpenSettings && (
          <button onClick={onOpenSettings} title="Paramètres du compte" style={btnGhost(C)}>
            <Settings size={15} />
          </button>
        )}
        <button onClick={onClose} title="Fermer" style={btnGhost(C)}>
          <X size={18} />
        </button>
      </div>

      {/* Corps 3 zones */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Sidebar dossiers */}
        <div style={{ width: 220, borderRight: `1px solid ${C.border}`, padding: 10, overflowY: 'auto', flexShrink: 0 }}>
          {folders.length === 0 && <div style={{ fontSize: 12, color: C.faint, padding: 8 }}>Chargement…</div>}
          {folders.map((f) => {
            const Icon = folderIcon(f);
            const active = f.path === activeFolder;
            return (
              <button
                key={f.path}
                onClick={() => openFolder(f.path)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                  padding: '8px 10px', borderRadius: 8, border: 'none',
                  background: active ? C.accentBg : 'transparent',
                  color: active ? C.accent : C.text, cursor: 'pointer',
                  fontSize: 13, fontWeight: active ? 600 : 500, marginBottom: 2, textAlign: 'left',
                }}
              >
                <Icon size={15} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || f.path}</span>
              </button>
            );
          })}
        </div>

        {/* Liste messages */}
        <div style={{ width: 360, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <form onSubmit={onSearch} style={{ padding: 10, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'var(--input-bg, var(--card-bg))' }}>
              <Search size={14} color={C.faint} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Rechercher dans ${activeFolder}…`}
                style={{ flex: 1, border: 'none', background: 'transparent', color: C.text, fontSize: 13, outline: 'none' }}
              />
            </div>
          </form>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingList && <div style={{ padding: 16, color: C.faint, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={15} className="spin" /> Chargement…</div>}
            {!loadingList && messages.length === 0 && (
              <div style={{ padding: 24, color: C.faint, fontSize: 13, textAlign: 'center' }}>
                {query ? 'Aucun résultat.' : 'Aucun message dans ce dossier.'}
              </div>
            )}
            {!loadingList && messages.map((m) => {
              const isSel = selected && selected.uid === m.uid;
              return (
                <button
                  key={m.uid}
                  onClick={() => openMessage(m)}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none',
                    borderBottom: `1px solid ${C.border}`,
                    background: isSel ? C.accentBg : 'transparent',
                    padding: '10px 14px', cursor: 'pointer', display: 'block',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    {!m.seen && <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.accent, flexShrink: 0 }} />}
                    <span style={{ flex: 1, fontSize: 13, fontWeight: m.seen ? 500 : 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {addressLabel(m.from)}
                    </span>
                    <span style={{ fontSize: 11, color: C.faint, flexShrink: 0 }}>{formatDate(m.date)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: m.seen ? C.muted : C.text, fontWeight: m.seen ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.subject || '(sans objet)'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panneau lecture / composeur */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {composing ? (
            <Composer C={C} draft={draft} setDraft={setDraft} sending={sending} onSend={sendDraft} onCancel={() => setComposing(false)} />
          ) : loadingMsg ? (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: C.faint, gap: 8 }}>
              <Loader2 size={22} className="spin" /> Ouverture…
            </div>
          ) : selected ? (
            <Reader C={C} msg={selected} onReply={startReply} />
          ) : (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: C.faint, fontSize: 14, textAlign: 'center', padding: 24 }}>
              <div>
                <Mail size={40} color={C.border} style={{ marginBottom: 12 }} />
                <div>Sélectionne un message pour le lire.</div>
                <div style={{ fontSize: 12, marginTop: 6 }}>{total > 0 ? `${total} message(s) dans ${activeFolder}` : ''}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin 1s linear infinite}`}</style>
    </div>
  );
};

// ── Panneau de lecture ──
const Reader = ({ C, msg, onReply }) => (
  <>
    <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, color: C.text, fontWeight: 600 }}>{msg.subject || '(sans objet)'}</h2>
          <div style={{ fontSize: 13, color: C.muted }}><strong style={{ color: C.text }}>{addressLabel(msg.from)}</strong></div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>À : {addressLabel(msg.to)}{msg.cc && msg.cc.length ? ` · Cc : ${addressLabel(msg.cc)}` : ''}</div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{msg.date ? new Date(msg.date).toLocaleString('fr') : ''}</div>
        </div>
        <button onClick={onReply} style={btnPrimary(C)}><Reply size={15} /> Répondre</button>
      </div>
      {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {msg.attachments.map((att, i) => (
            <button key={i} onClick={() => downloadAttachment(att)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer', fontSize: 12 }}>
              <Paperclip size={13} />
              <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
              {att.content_base64 ? <Download size={13} color={C.accent} /> : <span style={{ fontSize: 10, color: C.faint }}>(trop lourde)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
    <div style={{ flex: 1, overflow: 'hidden' }}>
      {msg.html ? (
        <iframe
          title="mail-body"
          sandbox=""
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;color:#1a1a1a;background:#fff;padding:18px;margin:0;line-height:1.55}img{max-width:100%}a{color:#1D4ED8}</style></head><body>${msg.html}</body></html>`}
          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
        />
      ) : (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: 14, color: C.text, padding: 22, margin: 0, lineHeight: 1.6 }}>
          {msg.text || '(message vide)'}
        </pre>
      )}
    </div>
  </>
);

// ── Composeur ──
const Composer = ({ C, draft, setDraft, sending, onSend, onCancel }) => {
  const up = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'var(--input-bg, var(--card-bg))', color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  return (
    <>
      <div style={{ padding: '14px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={onCancel} style={btnGhost(C)}><ArrowLeft size={16} /></button>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: C.text }}>Nouveau message</div>
        <button onClick={onSend} disabled={sending} style={{ ...btnPrimary(C), opacity: sending ? 0.6 : 1 }}>
          {sending ? <Loader2 size={15} className="spin" /> : <Send size={15} />} {sending ? 'Envoi…' : 'Envoyer'}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input value={draft.to} onChange={up('to')} placeholder="À : destinataire@exemple.fr" style={inp} />
        <input value={draft.cc} onChange={up('cc')} placeholder="Cc (optionnel)" style={inp} />
        <input value={draft.subject} onChange={up('subject')} placeholder="Objet" style={inp} />
        <textarea
          value={draft.body_text || ''}
          onChange={up('body_text')}
          placeholder="Votre message…"
          style={{ ...inp, flex: 1, minHeight: 220, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
        />
        <div style={{ fontSize: 11, color: C.faint }}>
          🔒 L'envoi part de votre compte uniquement quand vous cliquez « Envoyer ». L'IA ne peut jamais envoyer à votre place.
        </div>
      </div>
    </>
  );
};

function btnPrimary(C) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
    borderRadius: 8, border: 'none', background: C.accent, color: '#fff',
    cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0,
  };
}
function btnGhost(C) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 9px',
    borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent',
    color: C.text, cursor: 'pointer', fontSize: 13, flexShrink: 0,
  };
}

export default MailInbox;
